import type { EntityKind } from '../types';

/**
 * COL-011B child-first HUD pictograms.
 *
 * Every icon is an original 24x24 stroke drawing in `currentColor`, so buttons read without
 * text and recolour with their state. Kept as markup strings (not DOM nodes) so the HUD can
 * inject them with `innerHTML`; the test DOM mock ignores that property harmlessly.
 */
export type HudIconId =
  | 'coin'
  | 'star'
  | 'build'
  | 'land'
  | 'floor'
  | 'sound'
  | 'muted'
  | 'globe'
  | 'close'
  | 'check'
  | 'collect'
  | 'upgrade'
  | 'move'
  | 'rotate'
  | 'remove'
  | 'cancel'
  | 'paint'
  | 'search'
  | 'lock'
  | 'flag'
  | 'eraser'
  | 'chevronDown'
  | 'chevronUp'
  | 'grid'
  | 'wait'
  | 'viewLeft'
  | 'viewRight'
  | 'zoomIn'
  | 'zoomOut'
  | EntityKind;

const S = 'fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"';

const PATHS: Record<HudIconId, string> = {
  coin: `<circle cx="12" cy="12" r="8.5" ${S}/><path d="M12 7.5v9M9.5 10h3.4a1.6 1.6 0 0 1 0 3.2H9.8M9.6 16.4h3.6" ${S}/>`,
  star: `<path d="M12 3.6l2.5 5.2 5.7.7-4.2 3.9 1.1 5.6L12 16.3 6.9 19l1.1-5.6L3.8 9.5l5.7-.7z" ${S}/>`,
  build: `<path d="M4 20l6.5-6.5" ${S}/><path d="M9.5 6.5l3-3 6.5 6.5-3 3z" ${S}/><path d="M12.5 3.5l-3 3 4 4" ${S}/>`,
  land: `<path d="M4 8.5l5-2.5 6 2.5 5-2.5v11l-5 2.5-6-2.5-5 2.5z" ${S}/><path d="M9 6v11M15 8.5v11" ${S}/>`,
  floor: `<rect x="4" y="4" width="16" height="7" rx="2" ${S}/><path d="M20 7.5h1.5v4.5H12v3" ${S}/><rect x="10.5" y="15" width="3" height="5.5" rx="1" ${S}/>`,
  sound: `<path d="M4 10v4h3.5l4.5 3.5V6.5L7.5 10z" ${S}/><path d="M15.5 9.5a3.5 3.5 0 0 1 0 5M18 7a7 7 0 0 1 0 10" ${S}/>`,
  muted: `<path d="M4 10v4h3.5l4.5 3.5V6.5L7.5 10z" ${S}/><path d="M16 9.5l4.5 5M20.5 9.5l-4.5 5" ${S}/>`,
  globe: `<circle cx="12" cy="12" r="8.5" ${S}/><path d="M3.5 12h17M12 3.5c3 3 3 14 0 17M12 3.5c-3 3-3 14 0 17" ${S}/>`,
  close: `<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" ${S}/>`,
  check: `<path d="M5 12.5l4.5 4.5L19 7.5" ${S}/>`,
  collect: `<ellipse cx="12" cy="7.5" rx="6.5" ry="2.8" ${S}/><path d="M5.5 7.5v4c0 1.5 3 2.8 6.5 2.8s6.5-1.3 6.5-2.8v-4M5.5 11.5v4c0 1.5 3 2.8 6.5 2.8s6.5-1.3 6.5-2.8v-4" ${S}/>`,
  upgrade: `<path d="M12 19V6.5M6.5 12L12 6.5 17.5 12" ${S}/><path d="M6 4h12" ${S}/>`,
  move: `<path d="M12 3.5v17M3.5 12h17M9 6.5L12 3.5l3 3M9 17.5l3 3 3-3M6.5 9L3.5 12l3 3M17.5 9l3 3-3 3" ${S}/>`,
  rotate: `<path d="M18.5 12a6.5 6.5 0 1 1-2.2-4.9" ${S}/><path d="M18.5 4.5v4h-4" ${S}/>`,
  remove: `<path d="M5 7h14M9.5 7V4.8h5V7M7 7l.8 12.2h8.4L17 7M10.2 10.5v6M13.8 10.5v6" ${S}/>`,
  cancel: `<circle cx="12" cy="12" r="8.5" ${S}/><path d="M8.5 8.5l7 7M15.5 8.5l-7 7" ${S}/>`,
  paint: `<path d="M12 3.5c4.7 0 8.5 3.4 8.5 7.7 0 2.1-1.5 3.3-3.2 3.3h-1.8c-1 0-1.7.9-1.3 1.8.4.8.3 1.7-.4 2.4-.5.5-1.2.8-1.8.8-4.7 0-8.5-3.6-8.5-8S7.3 3.5 12 3.5z" ${S}/><circle cx="8" cy="11" r="1.2" fill="currentColor"/><circle cx="11" cy="7.5" r="1.2" fill="currentColor"/><circle cx="15.5" cy="8" r="1.2" fill="currentColor"/>`,
  search: `<circle cx="10.5" cy="10.5" r="6" ${S}/><path d="M15 15l5 5" ${S}/>`,
  lock: `<rect x="5" y="10.5" width="14" height="10" rx="2.5" ${S}/><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" ${S}/>`,
  flag: `<path d="M6 21V4" ${S}/><path d="M6 4.5h11l-2.5 3.5 2.5 3.5H6" ${S}/>`,
  eraser: `<path d="M4.5 15.5l9-9a2 2 0 0 1 2.8 0l3.2 3.2a2 2 0 0 1 0 2.8l-7.5 7.5H7.5z" ${S}/><path d="M9 11l5 5M4.5 20h15" ${S}/>`,
  chevronDown: `<path d="M6 9.5l6 6 6-6" ${S}/>`,
  chevronUp: `<path d="M6 14.5l6-6 6 6" ${S}/>`,
  viewLeft: `<path d="M14.5 5.5L7.5 12l7 6.5" ${S}/>`,
  viewRight: `<path d="M9.5 5.5L16.5 12l-7 6.5" ${S}/>`,
  zoomIn: `<path d="M12 6.5v11M6.5 12h11" ${S}/>`,
  zoomOut: `<path d="M6.5 12h11" ${S}/>`,
  grid: `<rect x="4" y="4" width="16" height="16" rx="2" ${S}/><path d="M4 12h16M12 4v16" ${S}/>`,
  wait: `<circle cx="12" cy="12" r="8.5" ${S}/><path d="M12 7.5V12l3 2" ${S}/>`,
  // catalog kinds
  starter_ride: `<path d="M3.5 10L12 4l8.5 6z" ${S}/><path d="M6 10v9M12 10v9M18 10v9M4.5 19h15" ${S}/><path d="M8 14.5h2.5M13.5 14.5H16" ${S}/>`,
  spinning_ride: `<path d="M5 9h11v5a5 5 0 0 1-5 5h-1a5 5 0 0 1-5-5z" ${S}/><path d="M16 10.5h2a2.5 2.5 0 0 1 0 5h-2" ${S}/><path d="M7 5.5c0-1 1-1 1-2M11 5.5c0-1 1-1 1-2" ${S}/>`,
  pirate_ship: `<path d="M3.5 14h17l-2.5 5H6z" ${S}/><path d="M12 14V4" ${S}/><path d="M12 4.5l6 5.5H12" ${S}/><path d="M12 6l-4.5 4H12" ${S}/>`,
  ferris_wheel: `<circle cx="12" cy="10.5" r="7" ${S}/><path d="M12 3.5v14M5 10.5h14M7.1 5.6l9.8 9.8M16.9 5.6l-9.8 9.8" ${S}/><path d="M8 20.5l4-3 4 3" ${S}/>`,
  roller_coaster: `<path d="M3 17c3-8 5-8 7-3s3 5 5-1a3 3 0 1 1 3 3" ${S}/><path d="M5 17v3.5M10 14.5V20.5M15 15.5v5" ${S}/>`,
  snack_kiosk: `<path d="M4 9.5L6 5h12l2 4.5" ${S}/><path d="M4 9.5c1.3 1.6 2.7 1.6 4 0 1.3 1.6 2.7 1.6 4 0 1.3 1.6 2.7 1.6 4 0 1.3 1.6 2.7 1.6 4 0" ${S}/><path d="M6 11v9h12v-9M10 20v-5h4v5" ${S}/>`,
  drink_kiosk: `<path d="M7 8.5h10l-1.2 11H8.2z" ${S}/><path d="M6 8.5h12" ${S}/><path d="M12 8.5l3-5.5" ${S}/><path d="M8.5 13.5h7" ${S}/>`,
  bench: `<path d="M4 9.5h16v3.5H4z" ${S}/><path d="M5.5 13v6.5M18.5 13v6.5M4 16.5h16" ${S}/>`,
  tree: `<path d="M12 3.5l6 8h-3.5l3.5 5H6l3.5-5H6z" ${S}/><path d="M12 16.5v4" ${S}/>`,
  lamp: `<path d="M12 20.5V9" ${S}/><path d="M9 20.5h6" ${S}/><path d="M8.5 9L12 3.5 15.5 9z" ${S}/><path d="M9 9h6v2.5H9z" ${S}/>`,
};

export function hudIconMarkup(icon: HudIconId): string {
  return `<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">${PATHS[icon]}</svg>`;
}

export function isHudIconId(value: string): value is HudIconId {
  return value in PATHS;
}
